import { Phone, Check, ArrowRight } from "lucide-react";
import { BRAND, SERVICES } from "../data/site";

const Services = () => {
  return (
    <div data-testid="services-page" className="pt-32">
      {/* Intro */}
      <section className="bg-stone-50 pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="uppercase text-xs tracking-[0.25em] text-stone-500 font-semibold mb-6">
            Services
          </p>
          <h1 className="font-display font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight text-stone-900 leading-[1.02] max-w-4xl">
            Remodeling services,
            <span className="block italic text-[#9B4819]">end to end.</span>
          </h1>
          <p className="mt-8 text-stone-600 text-base sm:text-lg max-w-2xl leading-relaxed">
            From one-room refreshes to full-home renovations, we design and build
            premium residential projects across Oregon and Washington.
          </p>
        </div>
      </section>

      {/* Alternating rows */}
      <section className="bg-white">
        {SERVICES.map((svc, idx) => {
          const reverse = idx % 2 === 1;
          return (
            <div
              key={svc.slug}
              data-testid={`service-${svc.slug}`}
              className={`border-t border-stone-200 py-20 md:py-28`}
            >
              <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                <div
                  className={`lg:col-span-6 ${reverse ? "lg:order-2" : ""}`}
                >
                  <div className="relative overflow-hidden group">
                    <img
                      src={svc.image}
                      alt={svc.title}
                      className="w-full h-[420px] md:h-[560px] object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                    />
                  </div>
                </div>
                <div className={`lg:col-span-6 ${reverse ? "lg:order-1 lg:pr-10" : "lg:pl-10"}`}>
                  <p className="uppercase text-xs tracking-[0.25em] text-stone-500 font-semibold mb-5">
                    {String(idx + 1).padStart(2, "0")} · {svc.title.split(" ")[0]}
                  </p>
                  <h2 className="font-display font-light text-4xl sm:text-5xl tracking-tight text-stone-900 leading-[1.05]">
                    {svc.title}
                  </h2>
                  <p className="mt-6 text-stone-600 text-base sm:text-lg leading-relaxed max-w-xl">
                    {svc.summary}
                  </p>
                  <ul className="mt-8 space-y-3 max-w-xl">
                    {svc.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-stone-800">
                        <Check className="w-5 h-5 text-[#9B4819] shrink-0 mt-1" strokeWidth={2.25} />
                        <span className="text-sm sm:text-base">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href={BRAND.phoneLink}
                    data-testid={`service-${svc.slug}-cta`}
                    className="mt-10 inline-flex items-center gap-3 bg-[#9B4819] hover:bg-[#7A3813] text-white px-7 py-4 text-sm font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_24px_-8px_rgba(155,72,25,0.5)]"
                  >
                    <Phone className="w-4 h-4" strokeWidth={2.25} />
                    Call {BRAND.phoneDisplay}
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      {/* Process */}
      <section data-testid="process-section" className="bg-stone-900 text-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="uppercase text-xs tracking-[0.25em] text-[#E8A976] font-semibold mb-6">
            How we work
          </p>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl">
            A clear, honest process.
          </h2>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-stone-800">
            {[
              { n: "01", t: "Consult", d: "We visit, measure, and listen. You get a plain-English scope and rough budget." },
              { n: "02", t: "Design", d: "Selections, layouts, and material samples. You approve everything before we start." },
              { n: "03", t: "Build", d: "One lead, one crew, one timeline. We protect your home and clean up daily." },
              { n: "04", t: "Handover", d: "Final walk-through, punch list, and photos. We stay in touch for the long haul." },
            ].map((step) => (
              <div key={step.n} className="bg-stone-900 p-8 lg:p-10">
                <div className="font-display text-5xl font-light text-[#9B4819]">
                  {step.n}
                </div>
                <h3 className="font-display text-xl mt-6 mb-3">{step.t}</h3>
                <p className="text-stone-400 text-sm leading-relaxed">{step.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
